import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Ladysmith = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ladysmith-bc:About Ladysmith Paragraph 1">
          Ladysmith, perched gracefully on the eastern shores of Vancouver
          Island in{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a town that seamlessly blends natural beauty with
          cultural charm. Its scenic location along the Strait of Georgia and
          proximity to the Coast Mountains creates breathtaking vistas.
          Ladysmith is steeped in history, evident in its well-preserved
          heritage district and annual Festival of Lights, a celebrated event
          that illuminates the town in holiday splendor. Outdoor enthusiasts are
          drawn to the area’s hiking trails, boating opportunities, and tranquil
          parks, while its thriving arts and cultural scene adds vibrancy. A
          strong sense of community, showcased in local festivals and events,
          complements the town’s warm ambiance. With amenities, a marina, and
          Transfer Beach Park for recreation, Ladysmith stands as a gem among
          the many diverse{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$900 - $1,900',
          bed2: '$1,200 - $2,400',
          bed3: '$1,800 - $2,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.ladysmith-bc:Apartment Hunting in Ladysmith Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Ladysmith
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Apartments for rent Ladysmith Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Ladysmith
              </LinkText>{' '}
              are a favorite among those who value ease and efficiency in their
              living arrangements. These units range from charming, older styles
              with unique architectural details to more modern, streamlined
              designs. Located close to downtown amenities and the waterfront,
              they provide convenient access to the town’s shops, cafes, and
              scenic walking trails.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Houses for rent Ladysmith Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Ladysmith
              </LinkText>{' '}
              are ideal for those who desire more space and a connection to the
              town’s quaint, small-town ambiance. These homes often come with
              yards or outdoor spaces, perfect for gardening or enjoying the
              mild coastal climate. The architectural styles in Ladysmith range
              from historic homes with rich character to more contemporary
              designs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Townhouses for rent Ladysmith Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Ladysmith
              </LinkText>{' '}
              offer a combination of communal living and private space. These
              multi-floor units, often part of friendly communities, are
              preferred by small families, couples, or individuals seeking a
              balance between having their own home and the convenience of
              shared maintenance responsibilities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Basements for rent Ladysmith Paragraph">
              Basements for rent in Ladysmith provide a more affordable housing
              option. These units, typically part of larger houses, offer a cozy
              and private living space for singles, students, or young
              professionals. While they may have limitations in terms of light
              and space, they are a budget-friendly way to enjoy living in this
              charming coastal town.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Condos for rent Ladysmith Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Ladysmith
              </LinkText>{' '}
              cater to those seeking a low-maintenance lifestyle. Many condos
              offer scenic views of the surrounding landscapes and are designed
              for comfort and convenience. They are popular among retirees,
              young professionals, and small families, often featuring community
              amenities like shared gardens or fitness areas.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Rooms for rent Ladysmith Paragraph">
              <LinkText href={links.room}>Rooms for rent in Ladysmith</LinkText>{' '}
              are an economical choice, commonly preferred by students, seasonal
              workers, or those in need of flexible living arrangements. This
              option involves sharing common areas like kitchens and living
              rooms, providing an opportunity for social interaction and
              community living in a friendly small-town setting.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Short term rentals Ladysmith Paragraph">
              Ladysmith, a town on the east coast of Vancouver Island in British
              Columbia, offers a variety of employment opportunities, mainly in
              retail, tourism, and the service sector. While it provides access
              to local schools, it is not particularly known for a wide range of
              academic institutions. The town sees some interest in short-term
              rentals, often peaking during the summer months when tourism
              increases due to its scenic charm and historical significance.
              This trend in short-term rental popularity, largely seasonal and
              driven by tourism, is different from larger urban centers or
              college towns where{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> are
              consistently in high demand throughout the year, especially during
              summer and at the beginning of school semesters.{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                The flexibility that short-term rentals offer is an important
                factor for many renters
              </LinkText>
              , particularly those looking for temporary accommodation to
              experience the unique character of Ladysmith or for seasonal work.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.ladysmith-bc:Cheap rentals Ladysmith Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Ladysmith
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Ladysmith, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.ladysmith-bc:Living in Ladysmith Paragraph">
            Based in the eastern coast of Vancouver Island, Ladysmith, BC is a
            hidden gem that offers a unique blend of coastal beauty and
            small-town charm. Surrounded by pristine beaches, lush forests, and
            stunning ocean views, this picturesque town is a paradise for
            outdoor enthusiasts. Whether you enjoy hiking along the scenic
            coastal trails, kayaking in the crystal-clear waters, or simply
            relaxing on the sandy shores, Ladysmith has it all. But it’s not
            just the natural beauty that makes Ladysmith special. The tight-knit
            community and warm hospitality create a sense of belonging and make
            it easy to form lasting connections. With its rich history, vibrant
            arts scene, and lively local events, there’s always something to
            discover and experience in Ladysmith. And when you need a break from
            the hustle and bustle, the peaceful ambiance and laid-back lifestyle
            offer the perfect retreat. Whether you’re a resident or a visitor,
            Ladysmith is a place where you can truly embrace coastal living and
            immerse yourself in the magic of small-town charm.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.ladysmith-bc:Living in Ladysmith Paragraph 2">
            If you’re not sure that Ladysmith is the right city for you, check
            out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ladysmith-bc:Popular Neighbourhoods Ladysmith Paragraph">
          Like any city, Ladysmith is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Ladysmith, Ladysmith Waterfront, North Ladysmith, and
          South Ladysmith. For more info about Ladysmith, you can consult this
          guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.ladysmith-bc:Local Landmarks Ladysmith Paragraph">
          Ladysmith, BC, boasts an array of remarkable landmarks that highlight
          its natural splendor and rich history. One standout landmark is
          Transfer Beach, a picturesque waterfront park that offers breathtaking
          views of the ocean and surrounding islands. Visitors can enjoy
          picnicking, swimming, and kayaking in this serene setting. Another
          notable landmark is the Ladysmith Maritime Society Marina, a bustling
          harbor that showcases the town’s strong ties to the sea. Here,
          visitors can admire a collection of beautifully restored historic
          vessels and even take a boat tour to explore the stunning coastline.
          The Ladysmith Museum is also a must-visit landmark, housing a
          fascinating collection of artifacts that tell the story of the town’s
          past. Additionally, the Ladysmith Waterfront Gallery is a hub for
          local artists, showcasing a diverse range of artwork and hosting
          regular exhibitions. These landmarks make Ladysmith, BC, an enchanting
          destination that celebrates its natural wonders and cultural heritage.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Ladysmith
